<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from "dashboard/composables/store";
import { useAlert } from "dashboard/composables";
import { emitter } from "shared/helpers/mitt";
import { BUS_EVENTS } from "shared/constants/busEvents";
import { copyTextToClipboard } from "shared/helpers/clipboard";

import Input from "dashboard/components-next/input/Input.vue";
import Button from "dashboard/components-next/button/Button.vue";
import MercadopagoDealProductsDialog from "./MercadopagoDealProductsDialog.vue";

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  deals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["dealUpdated"]);

const { t } = useI18n();
const route = useRoute();
const store = useStore();

// Moneda bloqueada por ahora: la cuenta opera en ARS.
const CURRENCY = "ARS";

const STATUS_STYLES = {
  pending: "text-n-amber-11",
  in_process: "text-n-amber-11",
  approved: "text-n-teal-11",
  rejected: "text-n-ruby-11",
  cancelled: "text-n-ruby-11",
  refunded: "text-n-slate-11",
};

const amount = ref("");
const description = ref("");

// Copia local mutable: permite reflejar la edición de productos del modal al
// instante, sin esperar a que el padre vuelva a consultar la conversación.
const localDeals = ref([...props.deals]);

const hasDeals = computed(() => localDeals.value.length > 0);
const hasMultipleDeals = computed(() => localDeals.value.length > 1);
const activeDealIndex = ref(0);
const activeDeal = computed(
  () => localDeals.value[activeDealIndex.value] || null,
);

// Al cambiar de conversación (deals es una referencia nueva), resincronizar y
// volver a la primera tarjeta.
watch(
  () => props.deals,
  (deals) => {
    localDeals.value = [...deals];
    activeDealIndex.value = 0;
  },
);

const dealPositionLabel = computed(
  () => `${activeDealIndex.value + 1}/${localDeals.value.length}`,
);

const goToPreviousDeal = () => {
  if (activeDealIndex.value > 0) activeDealIndex.value -= 1;
};
const goToNextDeal = () => {
  if (activeDealIndex.value < localDeals.value.length - 1) {
    activeDealIndex.value += 1;
  }
};

const dealProductsDialogRef = ref(null);

const openEditProductsDialog = () => {
  dealProductsDialogRef.value?.open();
};

// El agente eligió confirmar "re-precargar siempre": al guardar la edición de
// productos, el formulario se re-precarga con el nuevo total (watch de activeDeal).
const onDealProductsUpdated = (updatedDeal) => {
  localDeals.value.splice(activeDealIndex.value, 1, updatedDeal);
  emit("dealUpdated");
};

// Precarga editable: al elegir/navegar un deal se completa el formulario con sus
// valores, pero el monto (y el resto) quedan 100% editables antes de generar el link.
watch(
  activeDeal,
  (deal) => {
    if (!deal) return;
    amount.value = deal.amount_cents ? deal.amount_cents / 100 : "";
    description.value = deal.products?.length
      ? deal.products.map((p) => p.name).join(" + ")
      : deal.title || "";
  },
  { immediate: true },
);

const mercadopagoIntegration = useFunctionGetter(
  "integrations/getIntegration",
  "mercadopago",
);
const isConfigured = computed(() => !!mercadopagoIntegration.value?.enabled);

const currentChat = useMapGetter("getSelectedChat");
const isRichEditorEnabled = computed(() => {
  const channelType = currentChat.value?.meta?.channel;
  return (
    channelType === "Channel::WebWidget" || channelType === "Channel::Email"
  );
});

const paymentsGetter = useFunctionGetter(
  "mercadopago/getByConversation",
  computed(() => props.conversationId),
);
const payments = computed(() => paymentsGetter.value);
const uiFlags = useMapGetter("mercadopago/uiFlags");
const isCreating = computed(() => uiFlags.value.isCreating);

const isAmountValid = computed(() => Number(amount.value) > 0);

// Historial persistido en DB, aislado por conversación: se re-consulta cada vez
// que cambia la conversación activa (no vive solo en memoria ni se comparte entre chats).
watch(
  () => props.conversationId,
  (conversationId) => {
    if (!conversationId) return;
    store.dispatch("mercadopago/fetchByConversation", conversationId);
  },
  { immediate: true },
);

const formatAmount = (amountCents, currencyCode) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currencyCode,
  }).format((amountCents || 0) / 100);
};

const statusLabels = computed(() => ({
  pending: t("CONTACT_PANEL.MERCADOPAGO.STATUS.PENDING"),
  in_process: t("CONTACT_PANEL.MERCADOPAGO.STATUS.IN_PROCESS"),
  approved: t("CONTACT_PANEL.MERCADOPAGO.STATUS.APPROVED"),
  rejected: t("CONTACT_PANEL.MERCADOPAGO.STATUS.REJECTED"),
  cancelled: t("CONTACT_PANEL.MERCADOPAGO.STATUS.CANCELLED"),
  refunded: t("CONTACT_PANEL.MERCADOPAGO.STATUS.REFUNDED"),
}));

const statusLabel = (status) => statusLabels.value[status] || status;

const copyPaymentLink = async (payment) => {
  await copyTextToClipboard(payment.init_point);
  useAlert(t("CONTACT_PANEL.COPY_SUCCESSFUL"));
};

// Refresco manual del historial (sin tiempo real): el agente lo dispara cuando
// quiere confirmar si un pago ya se acreditó, sin recargar la página.
const refreshHistory = () => {
  store.dispatch("mercadopago/fetchByConversation", props.conversationId);
};

// Se inserta como texto plano + URL cruda (no markdown): en WhatsApp un
// hipervínculo no sirve, y el agente puede revisar/editar antes de enviar.
const insertLinkIntoEditor = (payment) => {
  const message = t("CONTACT_PANEL.MERCADOPAGO.PAY_MESSAGE_TEMPLATE", {
    url: payment.init_point,
  });
  const event = isRichEditorEnabled.value
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR;
  emitter.emit(event, message);
};

const generateLink = async () => {
  if (!isAmountValid.value || isCreating.value) return;

  try {
    const payment = await store.dispatch("mercadopago/createPaymentLink", {
      conversationId: props.conversationId,
      dealId: activeDeal.value?.id,
      amount: amount.value,
      description: description.value,
      currency: CURRENCY,
    });
    insertLinkIntoEditor(payment);
    useAlert(t("CONTACT_PANEL.MERCADOPAGO.CREATE_SUCCESS"));
  } catch (error) {
    useAlert(error.message || t("CONTACT_PANEL.MERCADOPAGO.CREATE_ERROR"));
  }
};

onMounted(() => {
  store.dispatch("integrations/get");
});
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <div v-if="!isConfigured" class="text-sm text-n-slate-11">
      {{ t("CONTACT_PANEL.MERCADOPAGO.NOT_CONFIGURED") }}
      <router-link
        :to="{
          name: 'settings_applications_integration',
          params: {
            accountId: route.params.accountId,
            integration_id: 'mercadopago',
          },
        }"
        class="text-n-blue-text hover:underline"
      >
        {{ t("CONTACT_PANEL.MERCADOPAGO.GO_TO_SETTINGS") }}
      </router-link>
    </div>

    <template v-else>
      <p v-if="!hasDeals" class="text-xs text-n-slate-11">
        {{ t("CONTACT_PANEL.MERCADOPAGO.MANUAL_MODE_HINT") }}
      </p>

      <div v-if="activeDeal" class="flex flex-col gap-2">
        <div
          class="p-3 rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak"
        >
          <div class="flex items-start justify-between gap-2">
            <h5
              class="text-sm font-medium text-n-slate-12 truncate"
              :title="activeDeal.title"
            >
              {{ activeDeal.title }}
            </h5>
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              color="slate"
              size="xs"
              class="flex-shrink-0"
              @click="openEditProductsDialog"
            />
          </div>
          <div
            v-if="activeDeal.products?.length"
            class="mt-1 flex flex-wrap gap-1"
          >
            <span
              v-for="product in activeDeal.products"
              :key="product.id"
              class="inline-flex items-center px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 text-xs truncate max-w-[140px]"
              :title="product.name"
            >
              {{ product.name }}
            </span>
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ formatAmount(activeDeal.amount_cents, activeDeal.currency) }}
          </p>
        </div>

        <div
          v-if="hasMultipleDeals"
          class="flex items-center justify-center gap-2"
        >
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="activeDealIndex === 0"
            @click="goToPreviousDeal"
          />
          <span class="text-xs text-n-slate-11">
            {{ dealPositionLabel }}
          </span>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="activeDealIndex === localDeals.length - 1"
            @click="goToNextDeal"
          />
        </div>

        <MercadopagoDealProductsDialog
          ref="dealProductsDialogRef"
          :deal="activeDeal"
          @updated="onDealProductsUpdated"
        />
      </div>

      <div class="flex flex-col gap-3">
        <Input
          v-model="amount"
          type="number"
          min="0"
          :label="t('CONTACT_PANEL.MERCADOPAGO.AMOUNT')"
          placeholder="0.00"
        />
        <Input
          v-model="description"
          type="text"
          :label="t('CONTACT_PANEL.MERCADOPAGO.DESCRIPTION')"
          :placeholder="t('CONTACT_PANEL.MERCADOPAGO.DESCRIPTION_PLACEHOLDER')"
        />
        <Input
          :model-value="CURRENCY"
          type="text"
          disabled
          :label="t('CONTACT_PANEL.MERCADOPAGO.CURRENCY')"
        />
        <Button
          :is-loading="isCreating"
          :disabled="!isAmountValid"
          size="sm"
          @click="generateLink"
        >
          {{ t("CONTACT_PANEL.MERCADOPAGO.GENERATE_BUTTON") }}
        </Button>
      </div>

      <div class="flex flex-col gap-2 border-t border-n-weak pt-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t("CONTACT_PANEL.MERCADOPAGO.HISTORY_TITLE") }}
          </span>
          <Button
            icon="i-lucide-refresh-cw"
            variant="ghost"
            color="slate"
            size="xs"
            :title="t('CONTACT_PANEL.MERCADOPAGO.REFRESH_HISTORY')"
            :is-loading="uiFlags.isFetching"
            :disabled="uiFlags.isFetching"
            @click="refreshHistory"
          />
        </div>

        <div v-if="payments.length" class="flex flex-col gap-2">
          <div
            v-for="payment in payments"
            :key="payment.id"
            class="flex items-center justify-between gap-2 text-sm"
          >
            <div class="flex flex-col min-w-0">
              <a
                v-if="payment.init_point"
                :href="payment.init_point"
                target="_blank"
                rel="noopener noreferrer"
                class="truncate text-n-blue-text hover:underline"
              >
                {{
                  payment.description ||
                  t("CONTACT_PANEL.MERCADOPAGO.DEFAULT_ITEM_TITLE")
                }}
              </a>
              <span v-else class="truncate text-n-slate-12">
                {{
                  payment.description ||
                  t("CONTACT_PANEL.MERCADOPAGO.DEFAULT_ITEM_TITLE")
                }}
              </span>
              <span class="text-n-slate-11">
                {{ formatAmount(payment.amount_cents, payment.currency) }}
              </span>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
              <Button
                v-if="payment.init_point"
                icon="i-lucide-clipboard"
                variant="ghost"
                color="slate"
                size="xs"
                @click="copyPaymentLink(payment)"
              />
              <span
                class="font-medium whitespace-nowrap"
                :class="STATUS_STYLES[payment.status]"
              >
                {{ statusLabel(payment.status) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
